import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import { QualificationQuestion } from '../../lib/api';
import { ResponseMap } from './types';
import { EDITABLE_STATUSES } from './constants';

interface QuestionInputProps {
  question: QualificationQuestion;
  responses: ResponseMap;
  qualificationStatus: string;
  isSupplier: boolean;
  onResponseChange: (questionId: number, value: string | number | boolean | string[], questionType: string) => void;
}

export function QuestionInput({
  question,
  responses,
  qualificationStatus,
  isSupplier,
  onResponseChange,
}: QuestionInputProps) {
  const canEdit = isSupplier && EDITABLE_STATUSES.includes(qualificationStatus);

  // Helper: map a stored text answer back to the matching choice.value,
  // because legacy MAB data saved the *label* ("Yes") but the template
  // defines each option as {value:"691", label:"Yes"}. A naive render
  // then left every radio unchecked because "Yes" !== "691".
  const labelToValue = (raw: string): string => {
    const opts = (question.choices || []);
    if (!raw) return raw;
    const needle = raw.trim().toLowerCase();
    for (const c of opts) {
      if (typeof c === 'string') {
        if (c.trim().toLowerCase() === needle) return c;
      } else {
        const obj = c as { value?: string; label?: string };
        if ((obj.label || '').trim().toLowerCase() === needle) return String(obj.value ?? obj.label ?? '');
        if ((obj.value || '').toString().trim().toLowerCase() === needle) return String(obj.value ?? '');
      }
    }
    return raw;
  };

  const getResponseValue = (): string | number | boolean | string[] | undefined => {
    const resp = responses[question.id];
    if (!resp) return undefined;

    // Legacy MAB data stored every answer in text_value regardless of
    // the question type (importer landed answers as plain strings).
    // Fall back to text_value when the type-specific field is empty so
    // yes_no radios, numeric and choice fields still light up when the
    // stored value came through the legacy path.
    switch (question.question_type) {
      case 'text':
      case 'textarea':
        return resp.text_value || '';
      case 'number':
      case 'rating': {
        if (resp.number_value !== null && resp.number_value !== undefined) return resp.number_value;
        const n = Number(resp.text_value);
        return Number.isFinite(n) && resp.text_value !== '' ? n : undefined;
      }
      case 'date':
        return resp.date_value || resp.text_value || '';
      case 'yes_no': {
        if (resp.boolean_value === true || resp.boolean_value === false) return resp.boolean_value;
        const t = (resp.text_value || '').trim().toLowerCase();
        if (t === 'yes' || t === 'true' || t === '1') return true;
        if (t === 'no' || t === 'false' || t === '0') return false;
        return undefined;
      }
      case 'single_choice': {
        if (resp.choice_value && resp.choice_value.length > 0) return resp.choice_value[0];
        if (resp.text_value) return labelToValue(resp.text_value);
        return '';
      }
      case 'multiple_choice': {
        if (resp.choice_value && resp.choice_value.length > 0) return resp.choice_value;
        if (resp.text_value) {
          // A single label or a comma-separated list of labels.
          return resp.text_value.split(',').map(s => labelToValue(s.trim())).filter(Boolean);
        }
        return [];
      }
      default:
        return resp.text_value || '';
    }
  };

  const value = getResponseValue();

  switch (question.question_type) {
    case 'text':
      return (
        <Input
          value={String(value || '')}
          onChange={(e) => onResponseChange(question.id, e.target.value, 'text')}
          disabled={!canEdit}
          placeholder="Enter your answer..."
        />
      );
    case 'textarea':
      return (
        <Textarea
          value={String(value || '')}
          onChange={(e) => onResponseChange(question.id, e.target.value, 'textarea')}
          disabled={!canEdit}
          placeholder="Enter your answer..."
          rows={3}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value != null ? String(value) : ''}
          onChange={(e) => onResponseChange(question.id, parseFloat(e.target.value), 'number')}
          disabled={!canEdit}
          placeholder="Enter a number..."
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={String(value || '')}
          onChange={(e) => onResponseChange(question.id, e.target.value, 'date')}
          disabled={!canEdit}
        />
      );
    case 'yes_no':
      return (
        <RadioGroup
          value={value === true ? 'yes' : value === false ? 'no' : ''}
          onValueChange={(v) => onResponseChange(question.id, v === 'yes', 'yes_no')}
          disabled={!canEdit}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id={`${question.id}-yes`} />
            <Label htmlFor={`${question.id}-yes`}>Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id={`${question.id}-no`} />
            <Label htmlFor={`${question.id}-no`}>No</Label>
          </div>
        </RadioGroup>
      );
    case 'single_choice': {
      // `question.choices` can contain plain strings or {value, label, score}
      // objects. Rendering the object directly inside <Label> crashed
      // QualificationDetail with "Objects are not valid as a React child".
      // Normalise to {value, label} first.
      const opts = (question.choices || []).map(c => (
        typeof c === 'string' ? { value: c, label: c } : { value: String((c as { value?: string }).value ?? ''), label: String((c as { label?: string }).label ?? (c as { value?: string }).value ?? '') }
      ));
      return (
        <RadioGroup
          value={String(value || '')}
          onValueChange={(v) => onResponseChange(question.id, v, 'single_choice')}
          disabled={!canEdit}
          className="space-y-2"
        >
          {opts.map((choice, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <RadioGroupItem value={choice.value} id={`${question.id}-${idx}`} />
              <Label htmlFor={`${question.id}-${idx}`}>{choice.label}</Label>
            </div>
          ))}
        </RadioGroup>
      );
    }
    case 'multiple_choice': {
      const arrayValue = Array.isArray(value) ? value : [];
      const opts = (question.choices || []).map(c => (
        typeof c === 'string' ? { value: c, label: c } : { value: String((c as { value?: string }).value ?? ''), label: String((c as { label?: string }).label ?? (c as { value?: string }).value ?? '') }
      ));
      return (
        <div className="space-y-2">
          {opts.map((choice, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <Checkbox
                id={`${question.id}-${idx}`}
                checked={arrayValue.includes(choice.value)}
                onCheckedChange={(checked) => {
                  const newValues = checked
                    ? [...arrayValue, choice.value]
                    : arrayValue.filter((v: string) => v !== choice.value);
                  onResponseChange(question.id, newValues, 'multiple_choice');
                }}
                disabled={!canEdit}
              />
              <Label htmlFor={`${question.id}-${idx}`}>{choice.label}</Label>
            </div>
          ))}
        </div>
      );
    }
    case 'rating':
      return (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <Button
              key={rating}
              variant={value === rating ? 'default' : 'outline'}
              size="sm"
              onClick={() => onResponseChange(question.id, rating, 'rating')}
              disabled={!canEdit}
            >
              {rating}
            </Button>
          ))}
        </div>
      );
    default:
      return (
        <Input
          value={String(value || '')}
          onChange={(e) => onResponseChange(question.id, e.target.value, 'text')}
          disabled={!canEdit}
        />
      );
  }
}
